import { extractFileNameFromUrl } from '@/helper/utils';
import { SurveyListItem } from '@/types/survey.types';
import { FileText } from 'lucide-react';
import React from 'react';

interface SurveyReviewAndCreateProps {
  survey: Partial<SurveyListItem>;
  file: File | null;
  isConfirmed: boolean;
  setIsConfirmed: (value: boolean) => void;
  onDownloadFile: () => void;
  isDownloading: boolean;
}

const SurveyReviewAndCreate: React.FC<SurveyReviewAndCreateProps> = ({
  survey,
  file,
  isConfirmed,
  setIsConfirmed,
  onDownloadFile,
  isDownloading,
}) => {
  const isEditMode = !!survey.id;
  const existingFileName =
    isEditMode && survey.odkFileUrl ? extractFileNameFromUrl(survey.odkFileUrl) : null;

  return (
    <div className="mx-auto text-[#621C43]">
      <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
        <h2 className="text-xl font-bold">
          {survey?.id ? 'Review & Update Survey' : 'Review & Create Survey'}
        </h2>
        <p className="text-base font-medium">
          {survey?.id
            ? 'Review all information before updating the survey.'
            : 'Review all information before creating the survey.'}
        </p>
      </div>

      <section className="px-5 py-2">
        <div className="grid md:grid-cols-2 gap-2">
          <div className="border-[0.5px] border-[#000] rounded-xl p-3 px-4">
            <h3 className="font-medium mb-4 text-base">Basic Information</h3>
            <div className="text-sm font-medium space-y-1">
              <div className="flex justify-between items-center">
                <span className="font-light">Survey Title</span>
                <span className="font-medium capitalize">{survey.title}</span>
              </div>
              <div>
                <div className="">
                  <span className="font-light">Status</span>{' '}
                  <div className="text-right -mt-5">
                    <span
                      className={`${
                        survey.status ? 'bg-pink-800' : 'bg-red-800'
                      } text-[#FFF8ED] text-xs font-semibold px-2 py-0.5 rounded-full ml-1`}
                    >
                      {survey.status ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-[0.5px] border-[#000] rounded-xl p-3 px-4">
            <h3 className="font-medium mb-4 text-base">Uploaded File</h3>
            {(file || survey.fileData) && (
              <div className="text-sm border border-[#621C43] bg-white rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <FileText className="text-[#621C43]" size={20} />
                  <div className="flex-1">
                    <p className="font-medium text-[#621C43]">
                      {file?.name || survey.fileData?.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {((file?.size || survey.fileData?.size || 0) / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">New</span>
                </div>
              </div>
            )}
            {isEditMode && !file && !survey.fileData && existingFileName && (
              <div className="text-sm bg-white rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1">
                    <FileText className="text-[#621C43]" size={20} />
                    <div className="flex-1">
                      <p className="font-medium text-[#621C43] break-all">{existingFileName}</p>
                      <p className="text-xs text-gray-500 mt-1">Current file</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl p-4 border bg-[#4A082D] bg-opacity-20 mt-2">
          <label className="flex items-start gap-3 text-sm text-[#621C43]">
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              className="mt-1  accent-[#621C43] w-3 h-3"
            />
            <span className="font-medium">
              I confirm that all the information provided is accurate and I want to{' '}
              <span className=" text-[#4A082D]">{survey?.id ? 'update' : 'create'}</span> this
              {survey?.id ? ' survey' : ' Survey'}.
              <br />
              <span className="text-xs text-[#4A082D] font-light">
                This action will{' '}
                {survey?.id
                  ? 'update the survey details according to the changes'
                  : 'create the survey'}
                .
              </span>
            </span>
          </label>
        </div>
      </section>
    </div>
  );
};

export default SurveyReviewAndCreate;
